import React, { useState, useEffect } from 'react'
import { Save, X, XCircle } from 'lucide-react'

function VehicleForm({ vehicle, onSubmit, onCancel, isSubmitting = false }) {
  const [formData, setFormData] = useState({
    registrationNumber: '',
    type: '',
    make: '',
    model: '',
    year: '',
    fuelType: 'diesel',
    capacity: '',
    engineNumber: '',
    chassisNumber: '',
    color: '',
    nextServiceKm: '',
    ownerName: '',
    ownerContact: '',
    purchaseDate: '',
    purchasePrice: '',
    insuranceNumber: '',
    insuranceProvider: '',
    insuranceExpiry: '',
    permitNumber: '',
    permitExpiry: '',
    fitnessExpiry: '',
    pollutionExpiry: '',
    roadTaxExpiry: '',

    notes: ''
  })

  const [errors, setErrors] = useState({})

  const vehicleTypes = [
    'Truck',
    'Tipper',
    'JCB',
    'Excavator',
    'Bulldozer',
    'Crane',
    'Dumper',
    'Tractor',
    'Trailer',
    'Mini Truck',
    'Pickup',
    'Other'
  ]

  const fuelTypes = [
    'Diesel',
    'Petrol',
    'CNG',
    'Electric',
    'Hybrid'
  ]



  useEffect(() => {
    if (vehicle) {
      // Ensure all fields have defined values to prevent controlled/uncontrolled input warnings
      setFormData({
        registrationNumber: vehicle.registrationNumber || '',
        type: vehicle.type || '',
        make: vehicle.make || '',
        model: vehicle.model || '',
        year: vehicle.year || '',
        fuelType: vehicle.fuelType || 'diesel',
        capacity: vehicle.capacity !== null && vehicle.capacity !== undefined ? vehicle.capacity : '',
        engineNumber: vehicle.engineNumber || '',
        chassisNumber: vehicle.chassisNumber || '',
        color: vehicle.color || '',
        nextServiceKm: vehicle.nextServiceKm !== null && vehicle.nextServiceKm !== undefined ? vehicle.nextServiceKm : '',
        ownerName: vehicle.ownerName || '',
        ownerContact: vehicle.ownerContact || '',
        purchaseDate: vehicle.purchaseDate || '',
        purchasePrice: vehicle.purchasePrice !== null && vehicle.purchasePrice !== undefined ? vehicle.purchasePrice : '',
        insuranceNumber: vehicle.insuranceNumber || '',
        insuranceProvider: vehicle.insuranceProvider || '',
        insuranceExpiry: vehicle.insuranceExpiry || '',
        permitNumber: vehicle.permitNumber || '',
        permitExpiry: vehicle.permitExpiry || '',
        fitnessExpiry: vehicle.fitnessExpiry || '',
        pollutionExpiry: vehicle.pollutionExpiry || '',
        roadTaxExpiry: vehicle.roadTaxExpiry || '',

        notes: vehicle.notes || ''
      })
    }
  }, [vehicle])

  const handleChange = (e) => {
    const { name, value } = e.target
    
    // Handle numeric fields - convert empty string to empty string (not null)
    let processedValue = value
    const numericFields = ['year', 'currentKm', 'purchasePrice']
    if (numericFields.includes(name) && value === '') {
      processedValue = '' // Keep as empty string to maintain controlled input
    }
    
    setFormData(prev => ({ ...prev, [name]: processedValue }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.registrationNumber.trim()) {
      newErrors.registrationNumber = 'Registration number is required'
    }

    if (!formData.type.trim()) {
      newErrors.type = 'Vehicle type is required'
    }

    if (!formData.make.trim()) {
      newErrors.make = 'Make is required'
    }

    if (!formData.model.trim()) {
      newErrors.model = 'Model is required'
    }

    if (!formData.year || formData.year === '') {
      newErrors.year = 'Year is required'
    } else if (!/^\d{4}$/.test(String(formData.year)) || parseInt(formData.year) < 1900 || parseInt(formData.year) > new Date().getFullYear() + 1) {
      newErrors.year = 'Please enter a valid year'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (validateForm()) {
      // Clean up the data before submitting
      const cleanedData = { ...formData }
      
      // Remove empty strings and convert to null for optional fields
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === '') {
          cleanedData[key] = null
        }
      })
      
      // Ensure numeric fields are properly handled
          const numericFields = ['year', 'nextServiceKm', 'purchasePrice']
    numericFields.forEach(field => {
      if (cleanedData[field] === '' || cleanedData[field] === null) {
        cleanedData[field] = null
      } else if (cleanedData[field]) {
        // Convert to number if it's a valid numeric string
        const numValue = parseFloat(cleanedData[field])
        if (!isNaN(numValue)) {
          cleanedData[field] = numValue
        }
      }
    })
      
      onSubmit(cleanedData)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">

      
      {/* Basic Information */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Registration Number */}
          <div>
            <label htmlFor="registrationNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Registration Number *
            </label>
            <input
              type="text"
              id="registrationNumber"
              name="registrationNumber"
              value={formData.registrationNumber}
              onChange={handleChange}
              className={`input ${errors.registrationNumber ? 'border-red-500 focus:ring-red-500' : ''}`}
              placeholder="e.g., TN 01 AB 1234"
            />
            {errors.registrationNumber && <p className="text-red-500 text-xs mt-1">{errors.registrationNumber}</p>}
          </div>

          {/* Type */}
          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
              Vehicle Type *
            </label>
            <select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleChange}
              className={`input ${errors.type ? 'border-red-500 focus:ring-red-500' : ''}`}
            >
              <option value="">Select type</option>
              {vehicleTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            {errors.type && <p className="text-red-500 text-xs mt-1">{errors.type}</p>}
          </div>

          {/* Make */}
          <div>
            <label htmlFor="make" className="block text-sm font-medium text-gray-700 mb-1">
              Make *
            </label>
            <input
              type="text"
              id="make"
              name="make"
              value={formData.make}
              onChange={handleChange}
              className={`input ${errors.make ? 'border-red-500 focus:ring-red-500' : ''}`}
              placeholder="e.g., Tata, Mahindra"
            />
            {errors.make && <p className="text-red-500 text-xs mt-1">{errors.make}</p>}
          </div>

          {/* Model */}
          <div>
            <label htmlFor="model" className="block text-sm font-medium text-gray-700 mb-1">
              Model *
            </label>
            <input
              type="text"
              id="model"
              name="model"
              value={formData.model}
              onChange={handleChange}
              className={`input ${errors.model ? 'border-red-500 focus:ring-red-500' : ''}`}
              placeholder="e.g., LPT 1613"
            />
            {errors.model && <p className="text-red-500 text-xs mt-1">{errors.model}</p>}
          </div>

          {/* Year */}
          <div>
            <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-1">
              Year *
            </label>
            <input
              type="number"
              id="year"
              name="year"
              min="1900"
              max={new Date().getFullYear() + 1}
              value={formData.year}
              onChange={handleChange}
              className={`input ${errors.year ? 'border-red-500 focus:ring-red-500' : ''}`}
              placeholder="2023"
            />
            {errors.year && <p className="text-red-500 text-xs mt-1">{errors.year}</p>}
          </div>

          {/* Fuel Type */}
          <div>
            <label htmlFor="fuelType" className="block text-sm font-medium text-gray-700 mb-1">
              Fuel Type
            </label>
            <select
              id="fuelType"
              name="fuelType"
              value={formData.fuelType}
              onChange={handleChange}
              className="input"
            >
              {fuelTypes.map(fuel => (
                <option key={fuel.toLowerCase()} value={fuel.toLowerCase()}>{fuel}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Technical Details */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Technical Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Capacity */}
          <div>
            <label htmlFor="capacity" className="block text-sm font-medium text-gray-700 mb-1">
              Capacity
            </label>
            <input
              type="text"
              id="capacity"
              name="capacity"
              value={formData.capacity}
              onChange={handleChange}
              className="input"
              placeholder="e.g., 16 Tons, 200 HP"
            />
          </div>

          {/* Engine Number */}
          <div>
            <label htmlFor="engineNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Engine Number
            </label>
            <input
              type="text"
              id="engineNumber"
              name="engineNumber"
              value={formData.engineNumber}
              onChange={handleChange}
              className="input"
              placeholder="Enter engine number"
            />
          </div>

          {/* Chassis Number */}
          <div>
            <label htmlFor="chassisNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Chassis Number
            </label>
            <input
              type="text"
              id="chassisNumber"
              name="chassisNumber"
              value={formData.chassisNumber}
              onChange={handleChange}
              className="input"
              placeholder="Enter chassis number"
            />
          </div>

          {/* Color */}
          <div>
            <label htmlFor="color" className="block text-sm font-medium text-gray-700 mb-1">
              Color
            </label>
            <input
              type="text"
              id="color"
              name="color"
              value={formData.color}
              onChange={handleChange}
              className="input"
              placeholder="e.g., White, Blue"
            />
          </div>

          {/* Next Service Time */}
          <div>
            <label htmlFor="nextServiceKm" className="block text-sm font-medium text-gray-700 mb-1">
              Next Service Time
            </label>
            <input
              type="text"
              id="nextServiceKm"
              name="nextServiceKm"
              value={formData.nextServiceKm}
              onChange={handleChange}
              className="input"
              placeholder="Next service in hrs"
            />
          </div>


        </div>
      </div>

      {/* Owner Information */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Owner Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Owner Name */}
          <div>
            <label htmlFor="ownerName" className="block text-sm font-medium text-gray-700 mb-1">
              Owner Name
            </label>
            <input
              type="text"
              id="ownerName"
              name="ownerName"
              value={formData.ownerName}
              onChange={handleChange}
              className="input"
              placeholder="Enter owner name"
            />
          </div>

          {/* Owner Contact */}
          <div>
            <label htmlFor="ownerContact" className="block text-sm font-medium text-gray-700 mb-1">
              Owner Contact
            </label>
            <input
              type="tel"
              id="ownerContact"
              name="ownerContact"
              value={formData.ownerContact}
              onChange={handleChange}
              className="input"
              placeholder="Enter contact number"
            />
          </div>
        </div>
      </div>

      {/* Purchase Information */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Purchase Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Purchase Date */}
          <div>
            <label htmlFor="purchaseDate" className="block text-sm font-medium text-gray-700 mb-1">
              Purchase Date
            </label>
            <input
              type="date"
              id="purchaseDate"
              name="purchaseDate"
              value={formData.purchaseDate}
              onChange={handleChange}
              className="input"
            />
          </div>

          {/* Purchase Price */}
          <div>
            <label htmlFor="purchasePrice" className="block text-sm font-medium text-gray-700 mb-1">
              Purchase Price (₹)
            </label>
            <input
              type="number"
              id="purchasePrice"
              name="purchasePrice"
              value={formData.purchasePrice}
              onChange={handleChange}
              className="input"
              placeholder="Enter amount"
            />
          </div>
        </div>
      </div>

      {/* Documents & Permits */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Documents & Permits</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Insurance Number */}
          <div>
            <label htmlFor="insuranceNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Insurance Number
            </label>
            <input
              type="text"
              id="insuranceNumber"
              name="insuranceNumber"
              value={formData.insuranceNumber}
              onChange={handleChange}
              className="input"
              placeholder="Enter policy number"
            />
          </div>

          {/* Insurance Provider */}
          <div>
            <label htmlFor="insuranceProvider" className="block text-sm font-medium text-gray-700 mb-1">
              Insurance Provider
            </label>
            <input
              type="text"
              id="insuranceProvider"
              name="insuranceProvider"
              value={formData.insuranceProvider}
              onChange={handleChange}
              className="input"
              placeholder="e.g., ICICI Lombard"
            />
          </div>

          {/* Insurance Expiry */}
          <div>
            <label htmlFor="insuranceExpiry" className="block text-sm font-medium text-gray-700 mb-1">
              Insurance Expiry
            </label>
            <input
              type="date"
              id="insuranceExpiry"
              name="insuranceExpiry"
              value={formData.insuranceExpiry}
              onChange={handleChange}
              className="input"
            />
          </div>

          {/* Permit Number */}
          <div>
            <label htmlFor="permitNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Permit Number
            </label>
            <input
              type="text"
              id="permitNumber"
              name="permitNumber"
              value={formData.permitNumber}
              onChange={handleChange}
              className="input"
              placeholder="Enter permit number"
            />
          </div>

          {/* Permit Expiry */}
          <div>
            <label htmlFor="permitExpiry" className="block text-sm font-medium text-gray-700 mb-1">
              Permit Expiry
            </label>
            <input
              type="date"
              id="permitExpiry"
              name="permitExpiry"
              value={formData.permitExpiry}
              onChange={handleChange}
              className="input"
            />
          </div>

          {/* Fitness Expiry */}
          <div>
            <label htmlFor="fitnessExpiry" className="block text-sm font-medium text-gray-700 mb-1">
              Fitness Expiry
            </label>
            <input
              type="date"
              id="fitnessExpiry"
              name="fitnessExpiry"
              value={formData.fitnessExpiry}
              onChange={handleChange}
              className="input"
            />
          </div>

          {/* Pollution Expiry */}
          <div className="md:col-span-2 lg:col-span-1">
            <label htmlFor="pollutionExpiry" className="block text-sm font-medium text-gray-700 mb-1">
              Pollution Expiry
            </label>
            <input
              type="date"
              id="pollutionExpiry"
              name="pollutionExpiry"
              value={formData.pollutionExpiry}
              onChange={handleChange}
              className="input"
            />
          </div>

          {/* Road Tax Expiry */}
          <div className="md:col-span-2 lg:col-span-1">
            <label htmlFor="roadTaxExpiry" className="block text-sm font-medium text-gray-700 mb-1">
              Road Tax Expiry
            </label>
            <input
              type="date"
              id="roadTaxExpiry"
              name="roadTaxExpiry"
              value={formData.roadTaxExpiry}
              onChange={handleChange}
              className="input"
            />
          </div>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
          Notes
        </label>
        <textarea
          id="notes"
          name="notes"
          rows={3}
          value={formData.notes}
          onChange={handleChange}
          className="input resize-none"
          placeholder="Enter any additional notes about the vehicle"
        />
      </div>

      {/* Form Actions */}
      <div className="flex flex-col sm:flex-row sm:justify-end gap-3 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary flex items-center justify-center space-x-2"
        >
          <X className="h-4 w-4" />
          <span>Cancel</span>
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              <span>{vehicle ? 'Update Vehicle' : 'Save Vehicle'}</span>
            </>
          )}
        </button>
      </div>
    </form>
  )
}

export default VehicleForm
